import { useStore } from '@nanostores/react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { memo, useState, type FC, type PropsWithChildren, type ReactNode } from 'react';
import { toast } from 'react-toastify';
import { Alert } from '~/components/ui/Alert';
import { Dialog, DialogActions, DialogButton } from '~/components/ui/Dialog';
import { StripeLogo } from '~/components/ui/logos/StripeLogo';
import { useGithubStore } from '~/lib/context/github';
import { importStore } from '~/lib/projects';
import { duplicateProject } from '~/lib/projects/forkProject';
import { projectStore } from '~/lib/stores/project';
import { stripeStore } from '~/lib/stores/stripe';
import { supabaseStore } from '~/lib/stores/supabase';
import { logger } from '~/utils/logger';
import { preventUserInteractions } from '~/utils/overlay';

export const DuplicateDialog = memo(() => {
  const isOpened = useStore(projectStore.duplicateModalOpen);

  const [duplicating, setDuplicating] = useState(false);

  const stripeConfigured = useStore(stripeStore.isConfigured);
  const supabaseConfigured = useStore(supabaseStore.isConfigured);
  const hasBeenDeployed = useStore(importStore).deploymentInfo !== undefined;
  const github = useGithubStore();
  const githubConnectedTo = useStore(github.connection);
  const githubConfigured = Boolean(githubConnectedTo.account) && githubConnectedTo.repo;

  const onClose = () => projectStore.duplicateModalOpen.set(false);

  const onDuplicate = async () => {
    setDuplicating(true);

    const reenable = preventUserInteractions();

    try {
      await duplicateProject();
    } catch (error) {
      reenable();

      setDuplicating(false);

      logger.error(error);

      toast.error('Failed to duplicate project.');
    }
  };

  return (
    <Dialog open={isOpened} onClose={onClose} maxWidth={600} contentClassName="p-6 flex flex-col items-center">
      <RadixDialog.DialogTitle className="text-lg font-semibold text-bolt-ds-textPrimary truncate mb-1">
        Duplicate a project
      </RadixDialog.DialogTitle>
      <p className="text-base text-bolt-ds-textSecondary text-center max-w-md">
        You're about to duplicate your project, this includes configurations of your integrations.
      </p>
      <div className="my-4.5 flex flex-col gap-3 self-stretch">
        {supabaseConfigured && <SupabaseInfoSection />}
        {stripeConfigured && <StripeInfoSection />}
        {(githubConfigured || hasBeenDeployed) && (
          <NotCopiedIntegrationsSections githubConfigured={githubConfigured} hasBeenDeployed={hasBeenDeployed} />
        )}
      </div>
      <DialogActions className="p-0">
        <DialogButton
          variant="primary"
          disabled={duplicating}
          onClick={onDuplicate}
          truncate={false}
          className="flex items-center gap-0"
        >
          {duplicating && <span className="i-svg-spinners:90-ring-with-bg size-4 mr-2 inline-block" />}
          Duplicate project
        </DialogButton>
      </DialogActions>
    </Dialog>
  );
});

const SupabaseInfoSection = memo(() => {
  const supabaseProject = useStore(supabaseStore.supabaseProject);

  return (
    <InfoSection
      title={
        <>
          <span className="i-logos:supabase-icon size-4" /> Supabase
        </>
      }
    >
      <div className="text-sm text-bolt-ds-textPrimary">Project</div>
      <div className="text-xs text-bolt-ds-textSecondary">
        {supabaseProject?.name ?? supabaseProject?.id ?? 'Project not found'}
        <span className="ml-2.5">{supabaseProject?.region}</span>
      </div>
    </InfoSection>
  );
});

const StripeInfoSection = memo(() => {
  const secretKey = useStore(stripeStore.partialKey);

  return (
    <InfoSection
      title={
        <>
          <StripeLogo size="xs" /> Stripe
        </>
      }
    >
      <div className="text-sm text-bolt-ds-textPrimary">API Key</div>
      <div className="text-xs text-bolt-ds-textSecondary">{secretKey}</div>
    </InfoSection>
  );
});

const InfoSection: FC<PropsWithChildren<{ title: ReactNode }>> = ({ title, children }) => (
  <div>
    <h3 className="text-bolt-ds-textSecondary text-xs mb-2 flex items-center gap-1.5">{title}</h3>
    <div className="py-3 px-4 gap-1 flex flex-col rounded-md border-px border-solid border-bolt-ds-borderOutline">
      {children}
    </div>
  </div>
);

const NotCopiedIntegrationsSections: FC<{ githubConfigured: boolean; hasBeenDeployed: boolean }> = ({
  githubConfigured,
  hasBeenDeployed,
}) => {
  if (!githubConfigured && !hasBeenDeployed) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3">
      {githubConfigured && (
        <Alert
          type="neutral"
          title="Your project will not be duplicated on GitHub"
          iconClassName="i-heroicons:information-circle"
        >
          In order to have the project on Github you will need to connect to a new repository.
        </Alert>
      )}
      {hasBeenDeployed && (
        <Alert
          iconClassName="i-heroicons:information-circle"
          type="neutral"
          title="Your project will not share the same Netlify publication URL"
        >
          You can publish this project to a new URL by clicking "Publish".
        </Alert>
      )}
    </div>
  );
};
